/*
 * A workspace's address uses a slug made from its name (LEA-499), not its id. The id stays the key
 * everywhere else: it is stored in nearly every table, and a Bison workspace added from Settings gets
 * `bison-<provider id>`, which says nothing about whose workspace it is. The three named by hand
 * already have ids equal to their slugs, so their addresses do not change.
 *
 * Only the browser translates. A link the server builds (Telegram, MCP, the calendar callback) names
 * the id, and `/w/<id>` keeps working: it resolves to the workspace and is replaced by its slug.
 */

interface NamedWorkspace {
  readonly id: string;
  readonly name: string;
}

const MAX_SLUG = 60;

/** Lowercase letters and digits joined by single hyphens; null when the name has neither. */
export function workspaceSlug(name: string): string | null {
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, MAX_SLUG)
    .replace(/^-+|-+$/g, "");
  return slug === "" ? null : slug;
}

/**
 * Each workspace's slug, by id. A slug two workspaces would share, or one equal to another workspace's
 * id, is not used: those workspaces keep their id as their address, so no address is ambiguous.
 */
export function workspaceSlugs(workspaces: readonly NamedWorkspace[]): Map<string, string> {
  const ids = new Set(workspaces.map((workspace) => workspace.id));
  const wanted = new Map<string, string>();
  const taken = new Map<string, number>();
  for (const workspace of workspaces) {
    const slug = workspaceSlug(workspace.name);
    if (slug === null) continue;
    wanted.set(workspace.id, slug);
    taken.set(slug, (taken.get(slug) ?? 0) + 1);
  }
  const slugs = new Map<string, string>();
  for (const workspace of workspaces) {
    const slug = wanted.get(workspace.id);
    const unique = slug !== undefined && taken.get(slug) === 1;
    const clashesWithAnId = slug !== undefined && slug !== workspace.id && ids.has(slug);
    slugs.set(workspace.id, unique && !clashesWithAnId ? slug : workspace.id);
  }
  return slugs;
}

/** The id an address segment names: an id first, then a slug; null when it names neither. */
export function workspaceIdFor(token: string, slugs: ReadonlyMap<string, string>): string | null {
  if (slugs.has(token)) return token;
  for (const [id, slug] of slugs) if (slug === token) return id;
  return null;
}

let known = new Map<string, string>();

/** Called with each workspace list the app reads, so addresses use the newest names. */
export function rememberWorkspaces(workspaces: readonly NamedWorkspace[]): void {
  known = workspaceSlugs(workspaces);
}

/** The address segment for a workspace: its slug when known, else its id. */
export function slugOfWorkspace(id: string): string {
  return known.get(id) ?? id;
}

/** The workspace id an address segment names, or the segment itself when no list says otherwise. */
export function idOfWorkspace(token: string): string {
  return workspaceIdFor(token, known) ?? token;
}
